import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {color, sizing} from "@khanacademy/wonder-blocks-tokens";

interface Props {
    /**
     * The text to display on the button.
     */
    title: string;
    /**
     * The icon to display on the button. This will show up on the left side of
     * the button.
     */
    iconComponent?: IconComponent | null | undefined;
    /**
     * Whether the button is disabled. The text will be greyed out and the
     * button will not be clickable.
     */
    disabled?: boolean;
    /**
     * The function to call when the button is clicked.
     */
    onClick: (e: React.SyntheticEvent) => unknown;
    /**
     * The size of the button. This corresponds to the WB Button sizes.
     */
    size?: "small" | "medium" | "large";
    /**
     * A test ID that will be added to the button for e2e testing purposes.
     */
    testId?: string;
    /**
     * Additional styles to apply to the button.
     */
    style?: StyleType;
    /**
     * Whether to only show the icon and not the text. If this is true then the
     * iconComponent prop must not be null/undefined.
     */
    iconOnly?: boolean;
    /**
     * For iconOnly=true this will show a spinner in place of the icon.
     * This should be set to true when the button is in a loading state.
     */
    spinner?: boolean;
}

/**
 * An icon which will show up in the button to indicate that the button is for
 * a certain OAuth provider. This is not for a phosphor icon, but instead for
 * an icon that is specific to the OAuth provider.
 */
type IconComponent = React.ComponentType<{
    /**
     * The icon should be disabled if the button is disabled.
     */
    disabled: boolean;
    /**
     * The icon will always have aria-hidden set to true.
     */
    ariaHidden: boolean;
    /**
     * The size of the icon. This currently always medium, but may change in
     * the future.
     */
    size?: "small" | "medium" | "large";
}>;
/**
 * A button that is used for logging in with OAuth. This component includes an
 * optional icon that will show up on the left side of the button, and the text.
 */
export const LoginButton = (props: Props): React.ReactElement => {
    const {
        title,
        size,
        disabled,
        testId,
        onClick,
        style,
        iconComponent: IconComponent,
        iconOnly,
        spinner,
    } = props;

    const iconContainerStyle =
        size === "large"
            ? styles.largeIconContainer
            : size === "small"
              ? styles.smallIconContainer
              : styles.mediumIconContainer;
    return (
        // @ts-expect-error - TS2745 - This JSX tag's 'children' prop expects type 'string' which requires multiple children, but only a single child was provided.ts(2745)
        <Button
            size={size}
            disabled={disabled}
            testId={testId}
            onClick={onClick}
            kind="secondary"
            style={[
                styles.button,
                iconOnly && styles.buttonIconOnly,
                disabled && styles.disabled,
                style,
            ]}
            aria-label={iconOnly ? title : null}
            title={iconOnly ? title : null}
            spinner={iconOnly && spinner}
        >
            <>
                <View
                    style={[
                        styles.iconContainer,
                        iconContainerStyle,
                        iconOnly ? styles.iconContainerIconOnly : null,
                    ]}
                >
                    {IconComponent != null ? (
                        <IconComponent
                            disabled={!!disabled}
                            ariaHidden={true}
                            size="medium"
                        />
                    ) : null}
                </View>
                {iconOnly ? "" : <span>{title}</span>}
            </>
        </Button>
    );
};

const styles = StyleSheet.create({
    button: {
        position: "relative",
        backgroundColor: color.white,
        color: color.offBlack,
        paddingInlineStart: sizing.size_320,
        paddingInlineEnd: sizing.size_320,
        width: "100%",
    },
    buttonIconOnly: {
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
    disabled: {
        color: color.offBlack32,
    },
    iconContainer: {
        position: "absolute",
        top: 0,
        insetInlineStart: 0,
        bottom: 0,
        margin: "auto",
        justifyContent: "center",
    },
    smallIconContainer: {
        width: sizing.size_160,
        height: sizing.size_160,
        marginInlineStart: sizing.size_080,
    },
    mediumIconContainer: {
        width: sizing.size_240,
        height: sizing.size_240,
        marginInlineStart: sizing.size_160,
    },
    largeIconContainer: {
        width: sizing.size_320,
        height: sizing.size_320,
        marginInlineStart: sizing.size_320,
    },
    iconContainerIconOnly: {
        position: "relative",
        marginInlineStart: 0,
    },
});
